import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import { useCandidateRejectionReasons } from "../../../common/components/functional/candidateRejection/useCandidateRejectionReasons";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { epochToString } from "../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { GroupedTable } from "../../../common/components/functional/GroupedTable/GroupedTable";
import useGroupedTableSearch from "../../../common/components/functional/GroupedTable/search/useGroupedTableSearch";
import {
  RejectionMessagePreview,
  RejectionMessagePreviewData,
} from "../../../common/components/functional/rejectionMessagePreview/RejectionMessagePreview";
import TalentBlacklistedIcon from "../../../common/components/functional/talentStatus/TalentBlacklistedIcon";
import {
  FlexVerticalAligned,
  UnderlineLink,
} from "../../../common/components/styled/styled";
import { getName, sortByName } from "../../../common/helpers/common";
import { leadingZerosDateFormat } from "../../../common/helpers/formatDateTime";
import { ItemGroup, ItemGroupLoadingState } from "../../invoices/invoiceAPI";
import { TALENT_STATUS_SORT_ORDER } from "../../orders/editOrder/constants";
import { TalentNameWithPreview } from "../../orders/editOrder/long/talentsStatus/TalentNameWithPreview";
import { AssignmentCoverLetter } from "../../talentProfile/assignmentCoverLetter/AssignmentCoverLetter";
import { hasPoolPro } from "../../talents/helpers";
import { PoolProPopper } from "../../talents/PoolProPopper";
import {
  APPLICANTS_STATUS_COLUMNS,
  APPLICANTS_STATUS_GROUPS_DEFAULT_VALUES,
  ApplicantStatus,
  ApplicantStatusGroupName,
} from "../constants";
import ApplicantsActionBanner from "./ApplicantsActionBanner";
import { ApplicantStatusIndicator } from "./ApplicantStatusIndicator";
import {
  getJobAdApplicantRejectedMessage,
  JobAdApplicant,
} from "./jobAdApplicantsAPI";
import {
  fetchJobAdApplicants,
  selectAllJobAdApplicants,
} from "./jobAdApplicantsSlice";

interface ApplicantGroupItemBase {
  id: string;
  applicant: JSX.Element;
}

type ApplicantGroupItem = ApplicantGroupItemBase;

interface Props {
  jobAdId: string | undefined;
}

const TalentApplicantsStatus = ({ jobAdId }: Props) => {
  const { t } = useTranslation("jobAds");

  const [selectedApplicants, setSelectedApplicants] = useState<string[]>([]);
  const [groups, setGroups] = useState<ItemGroup<ApplicantGroupItem>[]>(
    APPLICANTS_STATUS_GROUPS_DEFAULT_VALUES,
  );

  const [coverLetterModalProps, setCoverLetterModalProps] = useState({
    open: false,
    talentId: "",
    jobAdId: "",
    talentFullName: "",
  });

  const dispatch = useAppDispatch();

  const searchState = useGroupedTableSearch({
    position: { top: "5px", left: 0 },
    placeholder: t("searchForApplicants"),
  });

  useEffect(() => {
    jobAdId &&
      dispatch(
        fetchJobAdApplicants({
          jobAdId: jobAdId,
          pageSize: "1500",
          ...(searchState?.searchValue
            ? { search: searchState.searchValue }
            : {}),
        }),
      );
  }, [dispatch, jobAdId, searchState.searchValue]);

  const applicants = useAppSelector(selectAllJobAdApplicants);
  const rejectionReasons = useCandidateRejectionReasons();

  const itemCount = groups.reduce((acc, group) => acc + group.items.length, 0);

  const updateSelectedItems = (selectedItems: ApplicantGroupItem[]) => {
    setSelectedApplicants(selectedItems.map((item) => item.id));
  };

  const setGroupData = useCallback(
    (groupName: string, data: any[]) => {
      setGroups((prevData) =>
        prevData.map((group) =>
          group.name === groupName
            ? {
                ...group,
                items: data,
                status: ItemGroupLoadingState.SUCCEEDED,
              }
            : group,
        ),
      );
    },
    [setGroups],
  );

  const openCoverLetterDialog = useCallback(
    (talentId: string, talentFullName: string) => {
      setCoverLetterModalProps({
        open: true,
        talentId,
        jobAdId: jobAdId || "",
        talentFullName,
      });
    },
    [jobAdId],
  );

  const formatApplicant = useCallback(
    (applicant: JobAdApplicant) => {
      const emptyIndicator = <ApplicantStatusIndicator />;
      const acceptedIndicator = (
        <ApplicantStatusIndicator active color="success.main" />
      );
      const rejectionTooltipLabel = [
        applicant.rejectionReason
          ? `${t("common:rejectionReason")}: ${rejectionReasons[applicant.rejectionReason]}`
          : null,
        applicant.rejectionComment,
      ]
        .filter(Boolean)
        .join("\n");
      const rejectionTooltip =
        applicant.rejectionReason || applicant.rejectionComment ? (
          <>
            {applicant.rejectionReason && (
              <Typography fontSize="11px" fontWeight={600}>
                {t("common:rejectionReason")}:{" "}
                {rejectionReasons[applicant.rejectionReason]}
              </Typography>
            )}
            {applicant.rejectionComment && (
              <Typography
                fontSize="11px"
                fontWeight={400}
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {applicant.rejectionComment}
              </Typography>
            )}
          </>
        ) : undefined;
      const rejectedIndicator = (
        <ApplicantStatusIndicator
          active
          color="primary.main"
          tooltip={rejectionTooltip}
          tooltipLabel={rejectionTooltipLabel}
        />
      );

      // Handle undefined eezyAccepted as null
      const eezyAcceptedStatus = applicant.eezyAccepted ?? null;

      let statusComponents = {};
      if (eezyAcceptedStatus === null) {
        // If eezyAccepted is null or undefined, show as unprocessed
        statusComponents = {
          [ApplicantStatus.EezyAccepted]: emptyIndicator,
          [ApplicantStatus.EezyRejected]: emptyIndicator,
        };
      } else if (eezyAcceptedStatus) {
        // If eezyAccepted is true, show as accepted
        statusComponents = {
          [ApplicantStatus.EezyAccepted]: acceptedIndicator,
          [ApplicantStatus.EezyRejected]: emptyIndicator,
        };
      } else {
        // If eezyAccepted is false, show as rejected
        statusComponents = {
          [ApplicantStatus.EezyAccepted]: emptyIndicator,
          [ApplicantStatus.EezyRejected]: rejectedIndicator,
        };
      }

      const fullName = getName(applicant);

      const coverLetterLink = applicant.hasCoverLetter ? (
        <UnderlineLink
          onClick={() => {
            openCoverLetterDialog(applicant.id, fullName);
          }}
        >
          {t("order:showCoverLetter")}
        </UnderlineLink>
      ) : null;
      const applicantRow = (
        <FlexVerticalAligned>
          <TalentNameWithPreview
            talentId={applicant.id}
            fullName={fullName}
            status={applicant.status}
            hasCoverLetter={applicant.hasCoverLetter}
          />
          {applicant.eezyAccepted === false && applicant.rejectionReason && (
            <InfoTooltip
              title={rejectionTooltip}
              icon={
                <InfoOutlinedIcon
                  aria-label={rejectionTooltipLabel}
                  sx={{ height: 20, width: 20 }}
                />
              }
              slotProps={{
                tooltip: {
                  sx: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" },
                },
              }}
            />
          )}
          <RejectionMessagePreview
            enabled={!!applicant.rejectionMessageSentAt && !!jobAdId}
            cacheKey={`${jobAdId ?? ""}-${applicant.id}-${applicant.rejectionMessageSentAt ?? ""}`}
            fetchMessageDetails={async (): Promise<RejectionMessagePreviewData> => {
              if (!jobAdId) {
                return {};
              }
              return await getJobAdApplicantRejectedMessage(
                { jobAdId, talentId: applicant.id },
                { requestId: "none" },
              );
            }}
          />
          {applicant.blackListed ? <TalentBlacklistedIcon /> : null}
          {hasPoolPro(applicant) && <PoolProPopper talent={applicant} />}
        </FlexVerticalAligned>
      );

      return {
        id: applicant.id,
        applicant: applicantRow,
        link: coverLetterLink,
        appliedAt: epochToString(leadingZerosDateFormat, applicant.appliedAt),
        ...statusComponents,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openCoverLetterDialog, t, rejectionReasons],
  );

  useEffect(() => {
    if (applicants.length) {
      // Clear selected items when data changes
      updateSelectedItems([]);

      // Separate applicants into new and processed groups
      let acceptedApplicants: JobAdApplicant[] = [];
      let newApplicants: JobAdApplicant[] = [];
      let rejectedApplicants: JobAdApplicant[] = [];

      applicants.forEach((applicant) => {
        // Check if eezyAccepted is undefined or null to determine "new" applicants
        if (
          applicant.eezyAccepted === null ||
          applicant.eezyAccepted === undefined
        ) {
          newApplicants.push(applicant);
        } else {
          // Sort into accepted or rejected based on eezyAccepted
          if (applicant.eezyAccepted) {
            acceptedApplicants.push(applicant);
          } else {
            rejectedApplicants.push(applicant);
          }
        }
      });

      setGroupData(
        ApplicantStatusGroupName.Accepted,
        acceptedApplicants
          .sort(
            (a, b) =>
              TALENT_STATUS_SORT_ORDER.indexOf(a.status) -
                TALENT_STATUS_SORT_ORDER.indexOf(b.status) ||
              a.lastName.localeCompare(b.lastName) ||
              a.firstName.localeCompare(b.firstName),
          )
          .map((applicant) => formatApplicant(applicant)),
      );
      setGroupData(
        ApplicantStatusGroupName.New,
        newApplicants
          .sort((a, b) => a.appliedAt - b.appliedAt)
          .map((applicant) => formatApplicant(applicant)),
      );
      setGroupData(
        ApplicantStatusGroupName.Rejected,
        sortByName(rejectedApplicants).map((applicant) =>
          formatApplicant(applicant),
        ),
      );
    } else {
      // Reset groups if no applicants
      setGroups(APPLICANTS_STATUS_GROUPS_DEFAULT_VALUES);
    }
  }, [applicants, formatApplicant, setGroupData]);

  return (
    // TODO: make responsive
    <Box sx={{ maxWidth: "1092px" }}>
      <Box sx={{ backgroundColor: "white", borderRadius: "8px" }}>
        <GroupedTable
          columns={APPLICANTS_STATUS_COLUMNS}
          groups={groups}
          itemCount={itemCount}
          translation="jobAds"
          updateSelectedItems={updateSelectedItems}
          allowBorders
          tableHeaderBackgroundColor="#fff"
          search={searchState}
          clearSelectedItems={!selectedApplicants.length}
          disableSelectAllItems
        />
        {selectedApplicants.length > 0 && (
          <ApplicantsActionBanner
            selectedApplicantIds={selectedApplicants}
            jobAdId={jobAdId}
          />
        )}
      </Box>
      <AssignmentCoverLetter
        status={coverLetterModalProps}
        closeDialog={() =>
          setCoverLetterModalProps({
            open: false,
            talentId: "",
            jobAdId: "",
            talentFullName: "",
          })
        }
      />
    </Box>
  );
};

export default TalentApplicantsStatus;
